import React, { useState, useEffect } from "react";
import moment from "jalali-moment";
import {
  Box,
  Button,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  IconButton,
} from "@mui/material";
import { useNotifications } from "@toolpad/core/useNotifications";
import PopoverComponent from "../../components/PopoverComponent";
import {
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridRowParams,
} from "@mui/x-data-grid";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../context/auth";
import { ProcessForm } from "./components/process-form";
import { ProcessDetails } from "./components/process-details";
import { ProcessFormSubmit } from "./components/ProcessFormSubmit";
import {
  Process,
  Service,
  fetchProcesses,
  fetchServices,
  deleteProcess,
} from "../../api/processes";
import {
  ProcessSection,
  fetchProcessSections,
} from "../../api/process-sections";
import { FollowUpGroup, fetchFollowUpGroups } from "../../api/follow-up-groups";

export default function Processes() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [openForm, setOpenForm] = useState(false);
  const [openDetails, setOpenDetails] = useState(false);
  const [selectedProcess, setSelectedProcess] = useState<Process | null>(null);
  const [editingProcess, setEditingProcess] = useState<Process | null>(null);
  const [formData, setFormData] = useState<any>(null);
  const notifications = useNotifications();

  // Popover state for delete confirmation
  const [deletePopover, setDeletePopover] = useState<{
    open: boolean;
    anchorEl: HTMLElement | null;
    processId: number | null;
  }>({
    open: false,
    anchorEl: null,
    processId: null,
  });

  // Queries
  const {
    data: processes = [],
    isLoading,
    error,
    refetch: refetchProcesses,
  } = useQuery({
    queryKey: ["processes"],
    queryFn: () => fetchProcesses(user?.token || ""),
    enabled: !!user?.token,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  const { data: services = [], error: servicesError } = useQuery({
    queryKey: ["services"],
    queryFn: () => fetchServices(user?.token || ""),
    enabled: !!user?.token,
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: 3,
  });

  const { data: processSections = [], error: processSectionsError } = useQuery({
    queryKey: ["process-sections"],
    queryFn: () => fetchProcessSections(user?.token || ""),
    enabled: !!user?.token,
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: 3,
  });

  const { data: followUpGroups = [], error: followUpGroupsError } = useQuery({
    queryKey: ["follow-up-groups"],
    queryFn: () => fetchFollowUpGroups(user?.token || ""),
    enabled: !!user?.token,
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: 3,
  });

  // Error handling with useEffect
  useEffect(() => {
    if (error) {
      console.error("Error fetching processes:", error);
      notifications.show("خطا در بارگذاری فرایندها", {
        severity: "error",
      });
    }
  }, [error, notifications]);

  useEffect(() => {
    if (servicesError) {
      console.error("Error fetching services:", servicesError);
      notifications.show("خطا در بارگذاری خدمات", {
        severity: "error",
      });
    }
  }, [servicesError, notifications]);

  useEffect(() => {
    if (processSectionsError) {
      console.error("Error fetching process sections:", processSectionsError);
      notifications.show("خطا در بارگذاری بخش‌های فرایند", {
        severity: "error",
      });
    }
  }, [processSectionsError, notifications]);

  useEffect(() => {
    if (followUpGroupsError) {
      console.error("Error fetching follow-up groups:", followUpGroupsError);
      notifications.show("خطا در بارگذاری گروه‌های پیگیری", {
        severity: "error",
      });
    }
  }, [followUpGroupsError, notifications]);

  // Mutations (only delete mutation remains)

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteProcess(id, user?.token || ""),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["processes"] });
      notifications.show(response.message || "فرایند با موفقیت حذف شد", {
        severity: "success",
      });
    },
    onError: (error: Error) => {
      notifications.show(error.message || "خطا در حذف فرایند", {
        severity: "error",
      });
    },
  });

  // Handlers
  const handleCreate = () => {
    setEditingProcess(null);
    setOpenForm(true);
  };

  const handleEdit = (process: Process) => {
    setEditingProcess(process);
    setOpenForm(true);
  };

  const handleView = (process: Process) => {
    setSelectedProcess(process);
    setOpenDetails(true);
  };

  const handleDelete = (event: React.MouseEvent<HTMLElement>, id: number) => {
    setDeletePopover({
      open: true,
      anchorEl: event.currentTarget,
      processId: id,
    });
  };

  const handleDeleteConfirm = () => {
    if (deletePopover.processId) {
      deleteMutation.mutate(deletePopover.processId);
      setDeletePopover({
        open: false,
        anchorEl: null,
        processId: null,
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeletePopover({
      open: false,
      anchorEl: null,
      processId: null,
    });
  };

  const handleFormSuccess = () => {
    setOpenForm(false);
    setEditingProcess(null);
  };

  const handleFormError = (error: Error) => {
    console.error("Form error:", error);
  };

  const handleFormDataChange = (data: any) => {
    setFormData(data);
  };

  // Columns
  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "شناسه",
      width: 80,
    },
    {
      field: "title",
      headerName: "عنوان فرایند",
      width: 200,
    },
    {
      field: "serviceName",
      headerName: "خدمت",
      width: 150,
      renderCell: (params: any) => {
        return params.value || "نامشخص";
      },
    },
    {
      field: "createdOn",
      headerName: "تاریخ ایجاد",
      width: 150,
      renderCell: (params: any) => {
        if (!params.value) return "-";
        try {
          return moment(params.value).locale("fa").format("jYYYY/jMM/jDD");
        } catch (error) {
          console.error("Error formatting date:", error);
          return params.value;
        }
      },
    },
    {
      field: "modifiedOn",
      headerName: "آخرین تغییر",
      width: 150,
      renderCell: (params: any) => {
        if (!params.value) return "-";
        try {
          return moment(params.value).locale("fa").format("jYYYY/jMM/jDD");
        } catch (error) {
          console.error("Error formatting date:", error);
          return params.value;
        }
      },
    },
    {
      field: "actions",
      type: "actions",
      headerName: "عملیات",
      width: 120,
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          icon={<ViewIcon />}
          label="مشاهده"
          onClick={() => handleView(params.row)}
        />,
        <GridActionsCellItem
          icon={<EditIcon />}
          label="ویرایش"
          onClick={() => handleEdit(params.row)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="حذف"
          onClick={(event) => handleDelete(event, params.row.id)}
        />,
      ],
    },
  ];

  if (error || servicesError || processSectionsError || followUpGroupsError) {
    return (
      <Box p={3}>
        <Alert severity="error">
          خطا در بارگذاری داده‌ها:{" "}
          {error?.message ||
            servicesError?.message ||
            processSectionsError?.message ||
            followUpGroupsError?.message ||
            "خطای نامشخص"}
        </Alert>
        <Button
          variant="outlined"
          onClick={() => {
            refetchProcesses();
            // You can add refetch for other queries if needed
          }}
          sx={{ mt: 2 }}
        >
          تلاش مجدد
        </Button>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
        >
          فرایند جدید
        </Button>
      </Box>

      <Paper sx={{ height: 600, width: "100%" }}>
        <DataGrid
          rows={processes || []}
          columns={columns}
          loading={isLoading}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
          disableRowSelectionOnClick
          getRowId={(row) => row.id || Math.random()}
          sx={{
            "& .MuiDataGrid-cell": {
              textAlign: "right",
            },
            "& .MuiDataGrid-columnHeaders": {
              textAlign: "right",
            },
          }}
        />
      </Paper>

      {/* Form Dialog */}
      <Dialog
        open={openForm}
        onClose={() => setOpenForm(false)}
        maxWidth={false}
        fullWidth
        fullScreen
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #e0e0e0",
            pb: 2,
          }}
        >
          <Typography variant="h5" component="h2">
            {editingProcess ? "ویرایش فرایند" : "فرایند جدید"}
          </Typography>
          <IconButton
            onClick={() => setOpenForm(false)}
            size="small"
            sx={{ color: "text.secondary" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ flex: 1, overflow: "auto" }}>
          <ProcessForm
            process={editingProcess}
            services={services}
            processSections={processSections}
            followUpGroups={followUpGroups}
            onFormDataChange={handleFormDataChange}
          />
        </DialogContent>
        <DialogActions
          sx={{
            borderTop: "1px solid #e0e0e0",
            pt: 2,
            px: 3,
            pb: 3,
            gap: 2,
          }}
        >
          <Button
            onClick={() => setOpenForm(false)}
            variant="outlined"
            sx={{ minWidth: 100 }}
          >
            انصراف
          </Button>
          <ProcessFormSubmit
            process={editingProcess}
            services={services}
            processSections={processSections}
            followUpGroups={followUpGroups}
            formData={formData}
            onSuccess={handleFormSuccess}
            onError={handleFormError}
          />
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog
        open={openDetails}
        onClose={() => setOpenDetails(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>جزئیات فرایند</DialogTitle>
        <DialogContent>
          {selectedProcess && (
            <ProcessDetails process={selectedProcess} services={services} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetails(false)}>بستن</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Popover */}
      <PopoverComponent
        id="delete-popover"
        open={deletePopover.open}
        anchorEl={deletePopover.anchorEl}
        handlePopoverClose={handleDeleteCancel}
        handler={handleDeleteConfirm}
        isLoading={deleteMutation.isPending}
        caption="آیا از حذف این فرایند اطمینان دارید؟"
      />

      {/* Notifications handled via useNotifications */}
    </Box>
  );
}
